// A picked or dropped folder's file paths, as handout folders. Pure.
//
// Handout folders are one level (folders.ts), so a nested path is not a
// tree: `Campaign/Act 1/map.png` lands in the folder named `Campaign / Act 1`.

/** Windows writes these into any folder it has shown; never a handout. */
const SYSTEM_FILES = new Set(['thumbs.db', 'desktop.ini']);

/** The path's folder parts and its file name, empty segments and stray spaces dropped. */
const segments = (path: string) =>
  path
    .split('/')
    .map((s) => s.trim())
    .filter(Boolean);

/**
 * The folder a file at `path` lands in: its directory parts joined by
 * ' / ', or '' (the root) for a bare name. `path` is a picked file's
 * `webkitRelativePath` or a dropped entry's `fullPath` (leading slash).
 */
export function folderOfPath(path: string): string {
  return segments(path).slice(0, -1).join(' / ');
}

/** A dot-file, anything inside a dot-folder, or a Windows system file - skipped, never listed. */
export function isHiddenPath(path: string): boolean {
  const parts = segments(path);
  const name = parts.at(-1) ?? '';
  return parts.some((p) => p.startsWith('.')) || SYSTEM_FILES.has(name.toLowerCase());
}

/** The files of a folder, in order, each with the handout folder it lands in; hidden ones dropped. */
export function folderItems<F>(entries: readonly { file: F; path: string }[]): { file: F; folder: string }[] {
  return entries.filter((e) => !isHiddenPath(e.path)).map((e) => ({ file: e.file, folder: folderOfPath(e.path) }));
}
